import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Eye, Flag, Search, Trash2, X } from "lucide-react";
import "../../assets/Styles/User.css";
import "../../assets/Styles/Report.css";
import Loader from "../../Loader/Loader";
import Pagination from "../../components/common/pagination";
import DeletePopup from "../../components/common/Aresure";
import { Snackbar } from "../../utils/snackbar";
import { deletereport, reportlist } from "../../services/Mainservice";

const defaultPagination = {
  currentPage: 1,
  totalPages: 1,
  totalCount: 0,
  pageSize: 10,
};

const displayDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const uniqueReporters = (reports = []) => {
  const reporters = new Map();
  reports.forEach(({ reporter }) => {
    if (reporter?.id) reporters.set(reporter.id, reporter);
  });
  return [...reporters.values()];
};

const ReportDetails = ({ user, onClose, onDismiss }) => {
  if (!user) return null;

  return (
    <div
      className="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="report-details-title"
      onClick={onClose}
    >
      <div
        className="report-details-modal report-user-modal"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          className="report-modal-close"
          type="button"
          onClick={onClose}
          aria-label="Close report details"
        >
          <X />
        </button>
        <p className="report-eyebrow">Reported user</p>
        <div className="report-profile-heading">
          {user.image ? (
            <img src={user.image} alt="" />
          ) : (
            <span className="report-avatar large">
              {(user.FirstName || "?").charAt(0)}
            </span>
          )}
          <div>
            <h2 id="report-details-title">
              {user.FirstName || "Unknown user"}
            </h2>
            <p>{user.email || "No email"}</p>
          </div>
          <span className="report-count-badge">
            {user.reportCount || user.reports?.length || 0} reports
          </span>
        </div>

        <div className="report-user-summary">
          <p>
            <span>User ID</span>
            <strong>{user.id}</strong>
          </p>
          <p>
            <span>Location</span>
            <strong>{user.city || "—"}</strong>
          </p>
          <p>
            <span>Account</span>
            <strong>
              {user.isBlock
                ? "Blocked"
                : user.isSuspend
                  ? "Suspended"
                  : "Active"}
            </strong>
          </p>
        </div>

        <h3 className="report-history-title">Report history</h3>
        <div className="report-history">
          {(user.reports || []).map((report) => (
            <article
              className="report-history-item"
              key={report.reportId || report.id}
            >
              <div className="report-history-top">
                <div>
                  <span>Report #{report.reportId || report.id}</span>
                  <strong>{report.reason || "No reason provided"}</strong>
                </div>
                <button
                  type="button"
                  title="Dismiss this report"
                  onClick={() => onDismiss(report)}
                >
                  <Trash2 size={17} />
                </button>
              </div>
              <div className="report-history-meta">
                <p>
                  <span>Reported by</span>
                  <strong>{report.reporter?.FirstName || "Unknown"}</strong>
                  <small>{report.reporter?.email}</small>
                </p>
                <p>
                  <span>Qes</span>
                  <strong>
                    {report.postTitle ||
                      report.post?.Title ||
                      "Direct user report"}
                  </strong>
                  <small>{report.postId ? `Post #${report.postId}` : ""}</small>
                </p>
                <p>
                  <span>Chat ID</span>
                  <strong>{report.chatId || "—"}</strong>
                </p>
                <p>
                  <span>Reported at</span>
                  <strong>{displayDate(report.createdAt)}</strong>
                </p>
              </div>
            </article>
          ))}
        </div>
        <div className="report-modal-actions">
          <button className="cancel-btn" type="button" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const Report = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [reportedUsers, setReportedUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [pagination, setPagination] = useState(defaultPagination);
  const [totals, setTotals] = useState({ users: 0, reports: 0 });
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [notice, setNotice] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleError = useCallback(
    (error) => {
      if (error?.status === 401 || error?.code === 401) {
        localStorage.clear();
        navigate("/");
        return;
      }
      setNotice({
        open: true,
        message: error?.message || "Something went wrong",
        severity: "error",
      });
    },
    [navigate],
  );

  const getData = useCallback(
    async (requestedPage = page, showLoader = true) => {
      try {
        if (showLoader) setLoading(true);
        const response = await reportlist({
          page: requestedPage,
          pageSize: 10,
          search: search.trim(),
        });
        const result = response.data;
        const users = Array.isArray(result.data) ? result.data : [];
        setReportedUsers(users);
        setTotals({
          users: Number(result.total ?? users.length),
          reports: Number(result.totalReports ?? 0),
        });
        setPagination(
          result.pagination || {
            currentPage: result.currentPage || requestedPage,
            totalPages: result.totalPages || 1,
            totalCount: result.total ?? users.length,
            pageSize: 10,
          },
        );
      } catch (error) {
        handleError(error);
      } finally {
        setLoading(false);
      }
    },
    [handleError, page, search],
  );

  useEffect(() => {
    const timer = setTimeout(() => getData(page), search ? 400 : 0);
    return () => clearTimeout(timer);
  }, [getData, page, search]);

  useEffect(() => {
    if (!notice.open) return undefined;
    const timer = setTimeout(
      () => setNotice((value) => ({ ...value, open: false })),
      4000,
    );
    return () => clearTimeout(timer);
  }, [notice.open]);

  const requestDismiss = (report) => {
    setSelectedReport(report);
    setDetailsOpen(false);
    setDeleteOpen(true);
  };

  const handleDismiss = async () => {
    try {
      setSubmitting(true);
      const response = await deletereport(
        selectedReport.reportId || selectedReport.id,
      );
      setDeleteOpen(false);
      setNotice({
        open: true,
        message: response.data.message || "Report dismissed successfully",
        severity: "success",
      });
      await getData(page, false);
    } catch (error) {
      handleError(error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="container1 report-page">
        <div className="data_head report-head">
          <div>
            <h1 className="data_heading">Reports</h1>
            <p>
              {totals.reports} reports across {totals.users} reported user
              {totals.users === 1 ? "" : "s"}
            </p>
          </div>
          <div className="searchicon">
            <Search size={18} color="white" />
            <input
              type="search"
              value={search}
              placeholder="Search reported users..."
              aria-label="Search reported users"
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
            />
          </div>
        </div>

        <div className="data_table_over">
          <table className="data_table report-table">
            <thead>
              <tr>
                <th>Reported user</th>
                <th>Reports</th>
                <th>Reported by</th>
                <th>Latest reason</th>
                <th>Latest report</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {reportedUsers.map((user) => {
                const reporters = uniqueReporters(user.reports);
                const latest = user.reports?.[0];
                return (
                  <tr key={user.id}>
                    <td>
                      <div className="data_userdetails">
                        {user.image ? (
                          <img
                            src={user.image}
                            className="data_userdetails_img"
                            alt=""
                          />
                        ) : (
                          <span className="report-avatar">
                            {(user.FirstName || "?").charAt(0)}
                          </span>
                        )}
                        <div className="report-user-copy">
                          <strong>{user.FirstName || "Unknown user"}</strong>
                          <small>{user.email || "No email"}</small>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="report-count-badge">
                        {user.reportCount || user.reports?.length || 0}
                      </span>
                    </td>
                    <td>
                      <div className="report-user-copy">
                        <strong>
                          {reporters
                            .map((reporter) => reporter.FirstName)
                            .join(", ") || "Unknown"}
                        </strong>
                        <small>
                          {reporters.length} unique reporter
                          {reporters.length === 1 ? "" : "s"}
                        </small>
                      </div>
                    </td>
                    <td>
                      <span
                        className="report-reason-preview"
                        title={latest?.reason}
                      >
                        {latest?.reason || "No reason provided"}
                      </span>
                    </td>
                    <td>{displayDate(latest?.createdAt)}</td>
                    <td>
                      <div className="report-actions">
                        <button
                          type="button"
                          title="View all reports"
                          disabled={submitting}
                          onClick={() => {
                            setSelectedUser(user);
                            setDetailsOpen(true);
                          }}
                        >
                          <Eye size={19} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!loading && reportedUsers.length === 0 && (
            <div className="report-empty">
              <Flag size={35} />
              <p>
                {search
                  ? "No reported users match your search."
                  : "No reports have been submitted."}
              </p>
            </div>
          )}
        </div>
        <Pagination pagedata={pagination} onPageChange={setPage} />
      </div>

      {loading && <Loader />}
      <ReportDetails
        user={detailsOpen ? selectedUser : null}
        onClose={() => setDetailsOpen(false)}
        onDismiss={requestDismiss}
      />
      <DeletePopup
        isOpen={deleteOpen}
        setisOpen={setDeleteOpen}
        handleDelete={handleDismiss}
        onClose={() => setDeleteOpen(false)}
      />
      <Snackbar
        open={notice.open}
        message={notice.message}
        severity={notice.severity}
        onclose={() => setNotice((value) => ({ ...value, open: false }))}
      />
    </>
  );
};

export default Report;
